import { ExportFormat, FilterToggleButton, useAnalytics } from '@ecosystem/newron-client-lib';
import { layouts } from '@ecosystem/newron-design-system';
import Highcharts from 'highcharts';
import { cloneDeep } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import AveragePriceCard from '@pages/overview/average-selling-price-card/AveragePriceCard';
import { GrowthTypes } from '@components/comparison-growth-selector/Types';
import WarningProductGroupFilterContainer from '@components/containers/warning-pg-filter/WarningProductGroupFilterContainer';
import FilterChips from '@components/filter-chips/FilterChips';
import FilterSelectionNote from '@components/filter-selection-note/FilterSelectionNote';
import InfoModal from '@components/info-modal/InfoModal';
import LoadingOverlay from '@components/loading-overlay/LoadingOverlay';
import {
  constructCountryOption,
  getSelectedClientNameWithClientId,
  getSubscriptionForOtherProducts,
  mapDefaultSubscribedCountry,
  mapSubscribedCountries,
  onChangeHandlerForOtherProducts,
  OnRetailerChangeParam,
  retainTimeFrame,
  saveErrorLogForOnChange,
} from '@components/multi-banner-retailer-selector/MultiBannerRetailerSelectorUtils';
import PageHeader from '../../components/page-header/PageHeader';
import PerformanceFilter from '../../components/performance-filter/PerformanceFilter';
import ShareComparisonCard from '../../components/share-comparison-card/ShareComparisonCard';
import { Periodicity } from '@components/time-period-selector/PeriodsTypes';
import { KpiTextForTracking, PerformanceType } from '@const/PerformanceType';
import { AveragePriceChartResp, AverageSellingParam } from '@data-visualization/average-selling-price-chart/Types';
import { ShareComparisonChartResp, ShareComparisonParam } from '@data-visualization/share-comparison-chart/Types';
import { ClientSubscription } from '@models/Opa';
import { RetailerClient } from '@models/RetailerClient';
import { PerformanceParam, SalesPerformance } from '@models/SalesPerformance';
import { getEffectiveProductType } from '@models/SubscribedProductType';
import { getAverageSellingPriceChart, getShareComparisonChart } from '@services/Chart.service';
import { BaseTimePeriod, MainFilters, TimePeriod } from '@services/FiltersTypes';
import { getPerformance } from '@services/Performance.service';

import { getProductGroupsByCountry, getProductGroupsMasterByCountry } from '@services/ProductGroup.service';
import {
  actionCreators,
  actionCreatorsHitlistsRetailerClient,
  actionCreatorsInitialFilters,
  actionCreatorsInitialFiltersMarketPremium,
  actionCreatorsInitialFiltersProducts,
  actionCreatorsMarketPremium,
  actionCreatorsMarketPremiumClientState,
  actionCreatorsProducts,
  actionCreatorsRetailerClient,
  actionCreatorsRO,
  actionCreatorsSideFilters,
} from '@state/Index';
import { IRootState } from '@state/reducers/Reducers';
import { constructRetailerClientState } from '@state/reducers/retailer-client/RetailerClientUtils';
import { getAndStoreSavedFilters } from '@utils/AppSubscriptionUtils';
import { filterPGsByCountry, getCountryFilterPairsByTimeFrame, updateCountryAndPGFilters } from '@utils/CountryUtils';
import { retailerOverviewResponseConfig } from '@utils/FilterSettings';
import {
  checkPGsByCountryPrecondition,
  constructCountryOptionForSideMenu,
  getFeatureValueForRetailer,
  getFiltersForClient,
  getFiltersInitialState,
  getInitialAppliedCountries,
  getProductGroupFromSideFilters,
  getSubscriptionPeriodicitiesByCountryAndProductType,
  removeUnsavedFilters,
  RetailerDetails,
  updateAppliedCurrencyFilterByCountry,
  updateFieldsForOverviewAndCategoryLeaderboardAndExplore,
  updatePgsByCountryOnCountryApplyWithProductType,
  validateStoreFilterSettings,
} from '@utils/FilterUtils';
import { ErrorTagEnum, saveErrorLog } from '@utils/LogUtil';
import {
  checkPeriodExist,
  getAndUpdatePeriod,
  getPeriodicityForMATAndYTD,
  isBaseTimePeriodFormat,
} from '@utils/PeriodUtils';
import { isAuthorized } from '@utils/Permission';
import { getFeatureSelector } from '@utils/hook/selector/ShowPerformanceWithinSelector';
import { useSideMenuFilters } from '@utils/hook/sideFilters/SideFiltersHooks';
import {
  PAGE_VIEW_TRACKING_TITLE,
  pageViewTrackingFunction,
  PageViewTrackingFunctionParam,
} from '@utils/page-analytics/model/PageViewedTracking';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import {
  Country,
  FilterKeys,
  Filters,
  getPageTypeWithPageName,
  Option,
  PageName,
  PageType,
  PageTypeAndName,
} from '@utils/pageConfig/Types';

import { useChartExport } from '@ecosystem/export-utils';
import { SortingOptionsType } from '@components/design/migrated/Table';
import { RetailerOverviewCombinedChartInsight } from '@components/collections/retailer-overview-combined-chart/Model';
import {
  RelativePeriod,
  RelativePeriodLabel,
  RelativePeriodMapping,
} from '@components/comparison-period-selector/Types';
import {
  displayExportErrorToastMessage,
  getAppliedFiltersText,
  getKpiDisplayNameFromHeaderKey,
} from '@hooks/export/ExportUtils';
import {
  ExportCombinedChartOptions,
  useExportCombinedChart,
} from '@hooks/export/export-combined-chart/UseExportCombinedChart';
import { getExportTrackingFormat } from '@hooks/export/export-tracking/ExportTracking';
import getComparisonPeriod from '../../components/filter-selection-note/GetComparisonPeriod';
import PgAvailabilityBanner from '../../components/pg-availability-banner/PgAvailabilityBanner';
import TimeFramePickerWrapper from '../../components/time-frame-picker/components/wrapper/TimeFramePickerWrapper';
import { applyTimeFramePicker } from '@components/time-frame-picker/utils/TimeFramePickerUtils';
import { useToastMessage } from '@components/toast-message/ToastMessage';
import useDebouncedEffect from '../../hooks/UseDebouncedEffect';
import { byCountry } from '@state/reducers/initial-filter/InitialFiltersPredicates';
import { MainFiltersState } from '@state/reducers/main-filter/MainFiltersReducer';
import { getVolumeBasedOnPeriodType } from '@utils/factVolumeUtils/FactVolumeUtils';
import { useDispatchPgsAvailability } from '@utils/hook/incrementalLoading/UseDispatchPgsAvailability';
import { ElementType } from '@utils/page-analytics/model/AnalyticTrackingBasePropertiesObj';
import {
  dataExportedTrackingFunction,
  DataExportTrackingElementParam,
  useDataExportTracking,
} from '@utils/page-analytics/model/DataExportTracking';
import { getFallbackPeriodCombo, shouldResetComparisonPeriod } from '@utils/period-set/PeriodSetFallback';
import { useInitializedPeriodSet } from '@utils/period-set/UseInitializedPeriodSet';
import { useUpdatePerfPeriodSet } from '@utils/period-set/UseUpdatePerfPeriodSet';
import { retailerOverviewConfiguration } from '../Configuration';
import { AVERAGE_SELLING_PRICE, PERFORMANCE_CARD_TITLE, PERFORMANCE_TREND_TITLE } from '../Utilities';
import PageInfo from '../info/PageInfo';
import { ProductDataType, ProductType } from '../product-analyzer/Product';
import { Unauthorized } from '../unauthorized/Unauthorized';
import * as styles from './Overview.module.scss';
import { CollectionDataType } from '@ecosystem/newron-client-collections/build/shared/types';
import { RetailerOverviewLineChartInsight } from '@components/collections/retailer-overview-line-chart/Model';
import { ExportLineChartOptions, useExportMultiChart } from '@hooks/export/utilities/UseExportMultiChart';
import { CreateInsightDataFn } from '@ecosystem/newron-client-collections';
import { filterByAccessibleProductGroup } from '@utils/collectionUtils/CollectionUtils';
import { useUserPersistedFilters } from '@utils/hook/saveFilter/UseUserPersistedFilters';
import { getGrandTotalValue } from '@utils/TotalUtils';
import MarketPerformancePosFactsTiles from '@components/market-performance-pos-facts-tiles/MarketPerformancePosFactsTiles';
import { useExportImages } from '@hooks/export/UseExportChartImage';
import { generateUniqueChartKeys } from '@data-visualization/utilities/GenerateUniqueChartKeys';
import { RestoreLocalSavedViewProps } from '@models/SavedViews';
import { useRestoreRetailerOverviewSavedView } from '@utils/hook/saveViews/UseRestoreRetailerOverviewSavedView';
import { initialiseBaseFilters } from '@utils/InitBaseFilters';
import { useFactRevenueBySelectedCurrency } from '@hooks/UseFactRevenueBySelectedCurrency';
import { setApiErrorState } from '@services/utils/ErrorHandlers';

interface Props {
  retailerClients: RetailerClient[];
}

const supportedPeriodicities: Periodicity[] = retailerOverviewConfiguration.supportedPeriodicities;
const isDataError = (salesData: SalesPerformance) => {
  return JSON.stringify(salesData) === JSON.stringify(new SalesPerformance());
};
const Overview: React.FC<Props> = ({ retailerClients }) => {
  const pagesConfig = usePageConfig();
  const { refetch } = useUserPersistedFilters();
  const { Page } = layouts;
  const { track } = useAnalytics();

  const dispatch = useDispatch();
  const { updateMainFiltersRO } = bindActionCreators(actionCreatorsRO, dispatch);
  const { updateSideFilters } = bindActionCreators(actionCreatorsSideFilters, dispatch);
  const { updateRetailerClient } = bindActionCreators(actionCreatorsRetailerClient, dispatch);
  const { updateMarketPremiumRetailerClient } = bindActionCreators(actionCreatorsMarketPremiumClientState, dispatch);
  const { updateMainFiltersMarketPremium } = bindActionCreators(actionCreatorsMarketPremium, dispatch);
  const { updateInitialFilters } = bindActionCreators(actionCreatorsInitialFilters, dispatch);
  const { updateInitialFiltersMarketPremium } = bindActionCreators(actionCreatorsInitialFiltersMarketPremium, dispatch);
  const { updateMainFilters } = bindActionCreators(actionCreators, dispatch);
  const { updateInitialFiltersProducts } = bindActionCreators(actionCreatorsInitialFiltersProducts, dispatch);
  const { updateMainFiltersProducts } = bindActionCreators(actionCreatorsProducts, dispatch);
  const { updateHitlistsRetailerClient } = bindActionCreators(actionCreatorsHitlistsRetailerClient, dispatch);

  const subscribedProductTypes = useSelector(
    (state: IRootState) => state.subscribedProductTypes.subscribedProductTypes,
  );
  const effectiveProductType = getEffectiveProductType(subscribedProductTypes, PageName.RETAILER_OVERVIEW);

  const sideFiltersState = useSelector((state: IRootState) => state.sideFilters);
  const retailerClientState = useSelector((state: IRootState) => state.retailerClient);
  const initialFiltersState = useSelector((state: IRootState) => state.initialFilters);
  const mainFiltersState: MainFiltersState = useSelector((state: IRootState) => state.mainFilters);
  const mainFilterStateProducts = useSelector((state: IRootState) => state.mainFiltersProducts);
  const { selectedGrowthType } = useSelector((state: IRootState) => state.mainFiltersRO);
  const initialFiltersProductsState = useSelector((state: IRootState) => state.initialFiltersProducts);
  const hitlistsRetailerClientState = useSelector((state: IRootState) => state.hitlistsRetailerClient);

  const [onPageFirstLoad, setOnPageFirstLoad] = useState<boolean>(true);
  /*
   * Create a default filter to store all the initial filters that was passed from App component initially,
   * so that it can be reused in the page when change to other retailer. Please avoid putting this state in the useEffect.
   */
  const [defaultFilters, setDefaultFilters] = useState<MainFilters[]>(initialFiltersState.initialFilters);

  const { defaultCountry: initialCountry } = initialiseBaseFilters(
    pagesConfig,
    effectiveProductType,
    updateSideFilters,
    PageType.RETAILER_OVERVIEW,
  );
  const [appliedCountries, setAppliedCountries] = useState<Option[]>(initialCountry);
  const [selectedCountries, setSelectedCountries] = useState<Option[]>(initialCountry);

  //region variables and setters related to period set
  const { updatePeriod, updatePeriodicity, updateComparisonPeriod } = useUpdatePerfPeriodSet();
  const timePeriodOptions: TimePeriod = initialFiltersState.initialFilters.filter(
    byCountry(appliedCountries[0].value),
  )[0].timePeriod;
  const { period, periodicity, comparisonPeriod } = useInitializedPeriodSet(timePeriodOptions);

  //endregion

  const [filters, setFilters] = useState<MainFilters>(
    initialFiltersState.initialFilters.find((e: any) => e.country === selectedCountries[0].value) ??
      initialFiltersState.initialFilters[0],
  );

  const [isFetchingApi, setIsFetchingApi] = useState<boolean>(false);
  const [countryChanged, setCountryChanged] = useState<boolean>(false);
  const periodOptions = filters?.timePeriod;
  const CURRENT_TOTAL_NAME = getGrandTotalValue();
  const allFilters = [...pagesConfig.appliedFilters[PageType.RETAILER_OVERVIEW].selectedFilters];
  const pgInitialState = getFiltersInitialState(allFilters, FilterKeys.PRODUCT_GROUP);

  const [appliedProductGroups, setAppliedProductGroups] = useState<Option[]>(
    pgInitialState !== undefined ? pgInitialState.options : [],
  );

  const [selectedProductGroups, setSelectedProductGroups] = useState<Option[] | undefined>(
    pgInitialState !== undefined ? pgInitialState.options : undefined,
  );

  const [performanceType, setPerformanceType] = useState(PerformanceType.Value);
  const [shareVsValueVolumeFilterType, setShareVsValueVolumeFilterType] = useState(PerformanceType.Value);

  const [isApiError, setIsApiError] = useState<boolean>(false);

  const [salesPerformance, setSalesPerformance] = useState(new SalesPerformance());
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingShareVsValueVolume, setLoadingShareVsValueVolume] = useState<boolean>(true);
  const [shareVsValueVolume, setShareVsValueVolume] = useState(new ShareComparisonChartResp());
  const [shareComparisonParam, setShareComparisonParam] = useState<ShareComparisonParam>();
  const [loadingAveragePrice, setLoadingAveragePrice] = useState<boolean>(true);

  // Display overlay with loading icon when select other retailer in the dropdown
  const [loadingOverlay, setLoadingOverlay] = useState<boolean>(false);
  const [averagePriceParam, setAveragePriceParam] = useState<AverageSellingParam>();
  const [averagePrice, setAveragePrice] = useState(new AveragePriceChartResp());
  const countriesToQuery = (countries: Option[]) => {
    return pagesConfig.clientCountries.filter((x) => countries.some((y) => x.id === y.value));
  };

  const { exportImages, clearExportImage } = useExportImages(pagesConfig.pageType);

  const isAuthorizedUser = isAuthorized(pagesConfig.clientSubscription);
  const runPeriodTypeCheck = () => {
    const allSelectedFilters = [...pagesConfig.selectedFilters[PageType.RETAILER_OVERVIEW].selectedFilters];
    const countryIndex = allSelectedFilters.findIndex((e) => e.filterKey === FilterKeys.COUNTRY);
    return countryIndex !== -1
      ? checkPeriodExist(defaultFilters, countriesToQuery(allSelectedFilters[countryIndex].options)[0].id, periodicity)
      : checkPeriodExist(defaultFilters, countriesToQuery(appliedCountries)[0].id, periodicity);
  };

  const appliedCountry = (): Country | undefined => {
    return pagesConfig.clientCountries.filter((x) => appliedCountries.some((y) => x.id === y.value)).pop();
  };

  useDispatchPgsAvailability({
    periodValue: period.value,
    periodicity,
    appliedCountries,
    pgsByAppliedCountry: pagesConfig.pgsByAppliedCountry,
    countriesPgsAvailabilities: pagesConfig.masterPgsByCountry,
    appliedProductGroups,
  });

  // could be skipped? since period set is always well-formed
  const getPgToQuery = () => {
    if (!periodicity || !period.value || !appliedProductGroups || period.value.includes('NAN')) {
      return null;
    }

    if (
      (periodicity === 'Weekly' && !period.value.includes('Week')) ||
      (periodicity === 'Monthly' && period.value.includes('Week'))
    ) {
      return null;
    }

    const responseTimeFrame = runPeriodTypeCheck();

    if (responseTimeFrame !== null) {
      return appliedProductGroups;
    }
    return null;
  };

  const getPerformanceCardResponse = async (pType: PerformanceType) => {
    const pgToQuery = getPgToQuery();
    if (!pgToQuery) {
      return;
    }
    setIsApiError(false);
    setSalesPerformance(new SalesPerformance());
    setIsFetchingApi(true);
    try {
      const appliedFilters = [...pagesConfig.appliedFilters[PageType.RETAILER_OVERVIEW].selectedFilters];

      setLoading(true);
      const performanceParam: PerformanceParam = {
        clientId: retailerClientState.clientId,
        productType: effectiveProductType,
        productGroup: pgToQuery,
        countries: countriesToQuery(appliedCountries),
        period: period.value,
        //For future implementation of Custom Month, need to handle for BaseTimePeriod
        comparisonPeriod: comparisonPeriod as RelativePeriod,
        periodType: periodicity,
        saleType: pType,
        appliedFilters,
        isSummary: false,
      };
      const summary = await getPerformance(performanceParam);
      const data = pType === PerformanceType.Value ? summary.valuePerformance : summary.volumePerformance;
      setSalesPerformance(data);
    } catch (err: any) {
      saveErrorLog(err, 'Retailer Overview - Unable to get performance card response.', {
        category: ErrorTagEnum.ApiError,
        requestPayload: {
          clientId: retailerClientState.clientId,
          productGroup: appliedProductGroups,
          period: period.value,
          comparisonPeriod,
        },
      });
      setSalesPerformance(new SalesPerformance());
      setApiErrorState(err, setIsApiError);
    } finally {
      setLoading(false);
    }
    setIsFetchingApi(false);
  };

  const getShareComparisonChartResponse = async () => {
    const pgToQuery = getPgToQuery();
    if (!pgToQuery) {
      return;
    }

    setShareVsValueVolume(new ShareComparisonChartResp());
    setIsFetchingApi(true);
    try {
      const appliedFilters = [...pagesConfig.appliedFilters[PageType.RETAILER_OVERVIEW].selectedFilters];

      const shareComparisonParamObject: ShareComparisonParam = {
        clientId: retailerClientState.clientId,
        productType: effectiveProductType,
        productGroup: pgToQuery,
        countries: countriesToQuery(appliedCountries),
        period: period.value,
        //Future implementation of Custom Month, need to handle for BaseTimePeriod
        comparisonPeriod: comparisonPeriod as RelativePeriod,
        periodType: periodicity,
        saleType: shareVsValueVolumeFilterType,
        appliedFilters: appliedFilters,
        isSummary: false,
      };
      setLoadingShareVsValueVolume(true);
      setShareComparisonParam(shareComparisonParamObject);
      const data = await getShareComparisonChart(shareComparisonParamObject);
      setShareVsValueVolume(data);
    } catch (err: any) {
      saveErrorLog(err, 'Retailer Overview - Unable to get Share comparison chart response.', {
        category: ErrorTagEnum.ApiError,
        requestPayload: {
          clientId: retailerClientState.clientId,
          productGroup: appliedProductGroups,
          period: period.value,
          comparisonPeriod,
        },
      });
      setShareVsValueVolume(new ShareComparisonChartResp());
    } finally {
      setLoadingShareVsValueVolume(false);
      setIsFetchingApi(false);
    }
  };

  const getASPResponse = async () => {
    const pgToQuery = getPgToQuery();
    if (!pgToQuery) {
      return;
    }
    setAveragePrice(new AveragePriceChartResp());
    setIsFetchingApi(true);
    try {
      const appliedFilters = [...pagesConfig.appliedFilters[PageType.RETAILER_OVERVIEW].selectedFilters];

      const averageSellingParam: AverageSellingParam = {
        clientId: retailerClientState.clientId,
        productType: effectiveProductType,
        productGroup: pgToQuery,
        countries: countriesToQuery(appliedCountries),
        period: period.value,
        comparisonPeriod: comparisonPeriod as RelativePeriod,
        periodType: periodicity,
        saleType: shareVsValueVolumeFilterType,
        appliedFilters: appliedFilters,
        isSummary: false,
      };
      setLoadingAveragePrice(true);
      setAveragePriceParam(averageSellingParam);

      const avp = await getAverageSellingPriceChart(averageSellingParam);
      setAveragePrice(avp);
    } catch (err: any) {
      saveErrorLog(err, 'Retailer Overview - Unable to get Average selling price response.', {
        category: ErrorTagEnum.ApiError,
        requestPayload: {
          clientId: retailerClientState.clientId,
          productGroup: appliedProductGroups,
          period: period.value,
          comparisonPeriod,
        },
      });
      setAveragePrice(new AveragePriceChartResp());
    } finally {
      setLoadingAveragePrice(false);
      setIsFetchingApi(false);
      clearExportImage();
    }
  };

  useEffect(() => {
    const pageTypeAndName: PageTypeAndName = {
      pageType: PageType.RETAILER_OVERVIEW,
      pageName: PageName.RETAILER_OVERVIEW,
    };

    //Start Page View Amplitude Tracking
    //Update page viewing
    updateRetailerClient({ ...retailerClientState, pageViewing: PageName.RETAILER_OVERVIEW });

    const trackingParam: PageViewTrackingFunctionParam = {
      eventTitle: PAGE_VIEW_TRACKING_TITLE,
      sideFilters: sideFiltersState.appliedFilters,
      retailerClientState: retailerClientState,
      track: track,
      pageType: PageType.RETAILER_OVERVIEW, // Wont get from redux because of async
      pageName: PageName.RETAILER_OVERVIEW, // Wont get from redux because of async
      access: pagesConfig.access,
      initialLocationId: getInitialAppliedCountries(pagesConfig, PageType.RETAILER_OVERVIEW, effectiveProductType),
    };

    pageViewTrackingFunction(trackingParam);
    //End Page View Amplitude Tracking

    validateStoreFilterSettings(sideFiltersState, pagesConfig, pageTypeAndName);

    // set page config
    pagesConfig.setNavigateFromCategoryLeaderboard(false);
    pagesConfig.setIsNavigateFromOpportunity(false);
    pagesConfig.setIsNavigateFromPerfAnalysis(false);
    pagesConfig.setIsNavigateFromRS(true);
    pagesConfig.setIsHidePGInfoWarning(false);
    //needed to prevent race condition
    setOnPageFirstLoad(false);
  }, []);

  const onClick = (isExpanded: boolean) => {
    pagesConfig.toggleFiltersTray(isExpanded);
    resetSideFilter();
  };

  const resetSideFilter = () => {
    pagesConfig.setApplyFilters(false);
    pagesConfig.setClearFilters(false);
  };

  useEffect(() => {
    setTimeout(() => {
      for (let i = 0; i < Highcharts.charts.length; i++) {
        Highcharts?.charts[i]?.reflow();
      }
    }, 500);
  }, [pagesConfig.filtersTrayOpened]);

  useEffect(() => {
    const allAppliedFilters = [...pagesConfig.appliedFilters[PageType.RETAILER_OVERVIEW].selectedFilters];
    if (allAppliedFilters.length !== 0) {
      const pgIndex = allAppliedFilters.findIndex((e) => e.filterKey === FilterKeys.PRODUCT_GROUP);
      setAppliedProductGroups(pgIndex !== -1 ? cloneDeep(allAppliedFilters[pgIndex].options) : []);
    } else {
      setAppliedProductGroups([]);
    }

    const initialAppliedCountries = getInitialAppliedCountries(
      pagesConfig,
      PageType.RETAILER_OVERVIEW,
      ProductType.PerformanceAnalyzer,
    );
    setAppliedCountries(initialAppliedCountries);
    setSelectedCountries(initialAppliedCountries);
  }, [pagesConfig.appliedFilters]);

  /*
   * Updates selectedCountries when selected country is changed and differs from applied country
   * Updating the state will call another useEffect which helps to get the updated list of PGs
   * updates list of brands when select pgs (wip)
   */
  useEffect(() => {
    updateFieldsForOverviewAndCategoryLeaderboardAndExplore(
      pagesConfig,
      selectedCountries,
      setSelectedCountries,
      setSelectedProductGroups,
      updateSideFilters,
    );
  }, [pagesConfig.selectedFilters]);

  /*
   * calls API to get the list of product groups using selected countries
   */
  useEffect(() => {
    let pgsByCountry = pagesConfig.pgsByCountry;
    const clientId = retailerClientState.clientId;
    const filter = retailerOverviewResponseConfig.response[0];

    if (clientId === -1 || !selectedCountries || selectedCountries.length === 0) {
      return;
    }

    (async () => {
      if (periodicity) {
        pagesConfig.setSelectedTimeFrame(periodicity);
        const timeFrames = getSubscriptionPeriodicitiesByCountryAndProductType(
          effectiveProductType,
          pagesConfig,
          selectedCountries,
        );
        if (!timeFrames || timeFrames.length === 0) {
          return;
        }
        let pgs = await getProductGroupsByCountry(clientId, selectedCountries, effectiveProductType);

        pgsByCountry = filterPGsByCountry(pgs, selectedCountries);

        updateCountryAndPGFilters(
          pagesConfig,
          selectedCountries,
          pgsByCountry,
          filter,
          ProductType.PerformanceAnalyzer,
        );
        if (pagesConfig.masterPgsByCountry.length === 0) {
          let availableMasterPgs = await getProductGroupsMasterByCountry(
            clientId,
            appliedCountries,
            effectiveProductType,
          );
          pagesConfig.setPGsByAppliedCountry(pgsByCountry);
          pagesConfig.setMasterPgsByCountry(availableMasterPgs);
        }
      }
    })();
  }, [pagesConfig.clientId, selectedCountries, periodicity]);

  useEffect(() => {
    (async () => {
      const clientId = retailerClientState.clientId;
      let availableMasterPgs = pagesConfig.masterPgsByCountry;
      if (
        !checkPGsByCountryPrecondition(
          pagesConfig,
          clientId,
          periodicity,
          sideFiltersState.appliedFilters,
          appliedCountries,
        )
      ) {
        return;
      }
      updatePgsByCountryOnCountryApplyWithProductType(
        pagesConfig,
        clientId,
        selectedCountries,
        appliedCountries,
        effectiveProductType,
        undefined,
      );
      availableMasterPgs = await getProductGroupsMasterByCountry(clientId, appliedCountries, effectiveProductType);
      pagesConfig.setMasterPgsByCountry(availableMasterPgs);
    })();
  }, [pagesConfig.pgsByCountry]);

  useDebouncedEffect(() => {
    if (period.value !== '' && appliedProductGroups) {
      getPerformanceCardResponse(performanceType);
      getShareComparisonChartResponse();
      getASPResponse();
    }
    pagesConfig.setIsCountryChanged(false);
  }, [appliedProductGroups, period, appliedCountries]);

  useEffect(() => {
    const mainFilters = {
      timeFrame: periodicity,
      period: period,
      comparisonPeriod: comparisonPeriod,
      selectedGrowthType: mainFiltersState.selectedGrowthType,
    };
    //this is to bring the selected timeframe to TA page
    updateMainFiltersMarketPremium({
      ...mainFilters,
    });
    // when timeFrame is changed, whatever that is not applied in filter tray will be reset, selected countries and product groups reset
    removeUnsavedFilters(pagesConfig);
    setSelectedCountries(appliedCountries);
    setSelectedProductGroups(appliedProductGroups);
  }, [period, periodicity, comparisonPeriod]);

  // used to get main filters for the selected countries when country filter is changed
  useEffect(() => {
    if (appliedCountries !== undefined && selectedCountries[0] !== undefined) {
      const mainFilters = defaultFilters.find((e) => e.country === selectedCountries[0].value)!;
      // @ts-ignore
      setFilters(mainFilters);
      pagesConfig.setIsNavigateFromRS(false);

      const currentTimePeriodOptions = initialFiltersState.initialFilters.filter(
        byCountry(appliedCountries[0].value),
      )[0].timePeriod;

      const fallbackCombo = getFallbackPeriodCombo(
        { period, periodicity: periodicity, comparisonPeriod: comparisonPeriod },
        supportedPeriodicities,
        currentTimePeriodOptions,
        undefined,
        undefined,
        pagesConfig.pageName,
      );

      if (fallbackCombo !== null) {
        updatePeriod(fallbackCombo.period);
        updatePeriodicity(fallbackCombo.periodicity);

        if (shouldResetComparisonPeriod(comparisonPeriod)) {
          updateComparisonPeriod(fallbackCombo.comparisonPeriod);
        }
      }

      getAndUpdatePeriod(fallbackCombo ? fallbackCombo.period : period, currentTimePeriodOptions, updatePeriod);
    }
    if (!pagesConfig.isTimePeriodFirstLoad) {
      setCountryChanged(true);
    }
  }, [appliedCountries, pagesConfig.pageName]);

  useSideMenuFilters({
    clientId: pagesConfig.clientId,
    timeFrame: periodicity,
    selectedCountries: selectedCountries,
    selectedProducts: selectedProductGroups,
    appliedProductGroups,
    responseConfig: retailerOverviewResponseConfig,
    pagesConfig: pagesConfig,
    initialFilters: initialFiltersState.initialFilters,
    currentTotalName: CURRENT_TOTAL_NAME,
    productType: effectiveProductType,
  });

  /**
   * To handle the change event when select other retailer in the retailer dropdown - reset the necessary states in order to refresh the reports
   * @param subscriptions client subscription from opa policy
   */
  const onChangeHandler = async (subscriptions: ClientSubscription[]) => {
    setLoadingOverlay(true);
    const subscribedCountries = mapSubscribedCountries(subscriptions);

    // Default clientId, but the clientId should be always the same no matter how many countries the client has
    const defaultClientId = subscriptions[0].retailer;

    // Default country, retain the countries selection if exists in the subscription
    const defaultCountry = mapDefaultSubscribedCountry(subscriptions, appliedCountries);
    const defaultCountryCode = defaultCountry.countryCode;

    // Get the current side menu filters selection from pagesConfig
    const pagesConfigAllFilters = [...pagesConfig.appliedFilters];

    // get hitlists subscription. current assumption is that it exist
    const hitlistsSubscriptions = getSubscriptionForOtherProducts(
      ProductType.ProductAnalyzer,
      pagesConfig,
      defaultClientId,
    );
    const hitlistsSubscribedCountries = mapSubscribedCountries(hitlistsSubscriptions);

    //Retrieve client name
    const selectedClientName = getSelectedClientNameWithClientId(retailerClients, defaultClientId);

    if (hitlistsSubscribedCountries.length !== 0) {
      const onChangeROMethodParam: OnRetailerChangeParam = {
        subscriptions: hitlistsSubscriptions,
        appliedCountries,
        defaultClientId,
        pagesConfig,
        productType: ProductType.ProductAnalyzer,
        productDataType: ProductDataType.ProductAnalyzer,
        subscribedCountries: hitlistsSubscribedCountries,
        setLoadingOverlay: (_) => {},
        timeFrame: periodicity,
        pagesConfigAllFilters,
        pageName: PageName.RETAILER_OVERVIEW,
        pageList: [PageType.RI_PRODUCT],
      };

      await onChangeHandlerForOtherProducts(onChangeROMethodParam).then((response) => {
        if (!response) {
          return;
        }
        // if update to react 18 and above, can remove call to batch function
        batch(() => {
          updateInitialFiltersProducts({ initialFilters: response.filtersData });
          updateMainFiltersProducts({
            timeFrame: response.currTimeFrame,
            period: response.currPeriod,
            comparisonPeriod,
            selectedGrowthType: mainFilterStateProducts.selectedGrowthType,
            country: response.defaultCountryCode,
          });

          updateHitlistsRetailerClient(
            constructRetailerClientState(
              defaultClientId,
              hitlistsSubscribedCountries,
              selectedClientName,
              PageName.RETAILER_OVERVIEW,
            ),
          );
        });
      });
      constructCountryOptionForSideMenu(pagesConfigAllFilters, defaultCountry, [PageType.RI_PRODUCT]);
    }
    if (pagesConfig.isMultiCountryFeatureFlagPA) {
      // Construct new currency filter based on applied default country
      const currentlyAppliedFilters = pagesConfigAllFilters[PageType.RETAILER_OVERVIEW].selectedFilters;
      updateAppliedCurrencyFilterByCountry(
        pagesConfig,
        constructCountryOption(defaultCountry)[0],
        currentlyAppliedFilters,
      );
    }
    // Display saved filter according to selected retailer for both product types
    await getAndStoreSavedFilters(ProductType.PerformanceAnalyzer, defaultClientId, pagesConfig, pagesConfig.userId);

    // Call filters API
    const filtersData = await getFiltersForClient(
      defaultClientId,
      subscribedCountries,
      (_) => {},
      ProductType.PerformanceAnalyzer,
      ProductDataType.PerformanceAnalyzer,
      [],
    );

    const filtersDataBasedOnCountry = filtersData.find((filter) => filter.country === defaultCountryCode);

    constructCountryOptionForSideMenu(pagesConfigAllFilters, defaultCountry, [
      PageType.RETAILER_OVERVIEW,
      PageType.CATEGORY_LEADERBOARD,
      PageType.OPPORTUNITY_REVIEW,
      PageType.PERFORMANCE_ANALYSIS,
    ]);

    if (!filtersDataBasedOnCountry) {
      saveErrorLogForOnChange(pagesConfig.clientCountries, pagesConfig.appliedFilters, PageName.RETAILER_OVERVIEW);
      setLoadingOverlay(false);
      return;
    }

    const currTimeFrame = retainTimeFrame(filtersDataBasedOnCountry, periodicity);

    const storeFilterSettings = {
      appliedFilters: pagesConfigAllFilters,
    };
    const marketPremiumSubscription = getSubscriptionForOtherProducts(
      ProductType.MarketPremium,
      pagesConfig,
      defaultClientId,
    );
    const marketPremiumSubscribedCountries = mapSubscribedCountries(marketPremiumSubscription);
    resetSideFilter();

    // Update redux store with latest settings
    // if update to react 18 and above, can remove call to batch function
    batch(async () => {
      updateSideFilters(storeFilterSettings);

      updateMainFiltersRO({
        selectedGrowthType: GrowthTypes.percentage,
        country: defaultCountryCode,
      });

      updateRetailerClient(
        constructRetailerClientState(
          defaultClientId,
          subscribedCountries,
          selectedClientName,
          PageName.RETAILER_OVERVIEW,
        ),
      );
      updateInitialFilters({ initialFilters: filtersData });

      if (marketPremiumSubscribedCountries.length > 0) {
        updateInitialFiltersMarketPremium({ initialFilters: filtersData });
        updateMarketPremiumRetailerClient(
          constructRetailerClientState(
            defaultClientId,
            subscribedCountries,
            selectedClientName,
            PageName.RETAILER_OVERVIEW,
          ),
        );
        pagesConfig.setMarketPremiumClientId(defaultClientId);
        pagesConfig.setMarketPremiumClientCountries(subscribedCountries);
      }
      /**
       * The reset sequence below is very important, this is due to the massive useEffect that we have in the page
       */
      // Do not simply move the setState below, it will trigger unintended side effects
      pagesConfig.setSelectedFilters(pagesConfigAllFilters);
      pagesConfig.setClientCountries(subscribedCountries);
      if (hitlistsSubscriptions.length !== 0) {
        pagesConfig.setHitlistsClientCountries(hitlistsSubscribedCountries);
        pagesConfig.setHitlistsClientId(defaultClientId);
      }

      setDefaultFilters(filtersData);
      setFilters(filtersDataBasedOnCountry);
      setSelectedCountries(constructCountryOption(defaultCountry));

      pagesConfig.setSelectedTimeFrame(currTimeFrame);
      pagesConfig.setClientId(defaultClientId);
      setAppliedCountries(constructCountryOption(defaultCountry));

      // This is to update the selected feature in OR if the user changed to another retailer in Overview
      const productGroup = getProductGroupFromSideFilters(sideFiltersState.appliedFilters[PageType.OPPORTUNITY_REVIEW]);

      // Only update the selected feature in OR if the user has navigated to the page before
      if (productGroup) {
        const featureValues = await getFeatureValueForRetailer({
          clientId: defaultClientId,
          productType: effectiveProductType,
          productDataType: ProductDataType.PerformanceAnalyzer,
          selectedCountries: constructCountryOption(defaultCountry),
          timeFrame: currTimeFrame,
          selectedProductGroup: productGroup.options[0].value,
          currentTotalName: getGrandTotalValue(),
        } as RetailerDetails);

        updateMainFilters({
          feature: getFeatureSelector(featureValues, mainFiltersState.feature),
        });
      }
    });
    await refetch();
    setLoadingOverlay(false);
  };
  const applyTimeFramePickerCallback: (
    appliedPeriodicity: Periodicity,
    appliedPeriod: BaseTimePeriod,
    appliedRelativePeriod: RelativePeriod | BaseTimePeriod,
  ) => void = (appliedPeriodicity, appliedPeriod, appliedRelativePeriod) => {
    applyTimeFramePicker({
      givenPeriodicity: appliedPeriodicity,
      givenPeriod: appliedPeriod,
      givenComparison: appliedRelativePeriod,
      pageType: getPageTypeWithPageName(retailerClientState.pageViewing),
      pageName: retailerClientState.pageViewing,
      pagesConfig: pagesConfig,
      countryChanged: countryChanged,
      sideFiltersState: sideFiltersState,
      retailerClientState: retailerClientState,
      track: track,
      setTimeFrame: updatePeriodicity,
      setPeriod: updatePeriod,
      setRelativePeriod: updateComparisonPeriod,
      setCountryChanged: setCountryChanged,
    });
  };
  const filteredScopeData = getCountryFilterPairsByTimeFrame(pagesConfig.pgsByCountry, periodicity);
  const { showToastMessage } = useToastMessage();

  //Export Functionality
  const [invokeExportCombinedChart, onExportReadyCombinedChart] = useChartExport();
  //another set of callbacks for line chart
  const [invokeExportLineChart] = useChartExport();
  const { download: exportCombinedChart } = useExportCombinedChart();
  const { download: exportLineChart } = useExportMultiChart();

  const handleDataExportTrackingFunction = useDataExportTracking('mainFilters');

  const handleExportedTracking = (
    elementType: ElementType,
    exportFormats: ExportFormat[],
    elementKpi?: string,
    elementSort?: SortingOptionsType,
  ) => {
    const dataExportedTrackingParam: DataExportTrackingElementParam = {
      elementType,
      elementKpi,
      elementSort,
      optionChosen: exportFormats.map((format) => getExportTrackingFormat(format)),
    };
    handleDataExportTrackingFunction(dataExportedTrackingParam, dataExportedTrackingFunction);
  };

  const timePeriod = period.label;
  const periodType = getPeriodicityForMATAndYTD(period, periodicity).periodicity;
  const comparisonTimePeriod = getComparisonPeriod(period, periodType, comparisonPeriod);
  const appliedFiltersText: string = getAppliedFiltersText(pagesConfig, PageType.RETAILER_OVERVIEW);
  const requestPayload = {
    clientId: retailerClientState.clientId,
    productGroup: appliedProductGroups,
    period: period.value,
    comparisonPeriod: comparisonPeriod,
  };

  const selectedKpiOption: Option = {
    label: getKpiDisplayNameFromHeaderKey(
      pagesConfig.sortingOptions.headerKey,
      getVolumeBasedOnPeriodType(filters, periodicity),
    ),
    value: pagesConfig.sortingOptions.headerKey,
  };

  const factRevenueBySelectedCurrency = useFactRevenueBySelectedCurrency(
    pagesConfig,
    appliedCountries,
    effectiveProductType,
  );

  const onCombinedChartDownload = useCallback(
    async (exportFormats: ExportFormat[]): Promise<void> => {
      if (!shareComparisonParam) {
        return;
      }

      const volume = getVolumeBasedOnPeriodType(filters, periodicity);
      const isValue = shareVsValueVolumeFilterType === PerformanceType.Value;

      try {
        const data: ShareComparisonChartResp = await getShareComparisonChart({
          ...shareComparisonParam,
        });
        const exportCombinedChartOptions: ExportCombinedChartOptions = {
          exportFormats,
          invokeExport: invokeExportCombinedChart,
          title: PERFORMANCE_TREND_TITLE,
          appliedCountry: appliedCountries,
          appliedPg: appliedProductGroups,
          data: data,
          timePeriod: timePeriod,
          comparisonTimePeriod: comparisonTimePeriod,
          factRevenue: factRevenueBySelectedCurrency,
          periodType: periodType,
          showPerformanceWithin: '',
          appliedFiltersText: appliedFiltersText,
          selectedKpi: selectedKpiOption,
          chartSubtitle: isValue
            ? KpiTextForTracking(volume)[PerformanceType.Value]
            : KpiTextForTracking(volume)[PerformanceType.Volume],
          pageName: PageName.RETAILER_OVERVIEW,
          pagesConfig: pagesConfig,
        };
        await exportCombinedChart(exportCombinedChartOptions);
        handleExportedTracking(ElementType.COMBINED_CHART, exportFormats);
      } catch (err: any) {
        const errorMessage = 'Retailer Overview - export Performance Trend error.';
        displayExportErrorToastMessage(err, errorMessage, requestPayload, showToastMessage);
        return;
      }
    },
    [shareComparisonParam, shareVsValueVolume, shareVsValueVolumeFilterType, exportCombinedChart],
  );

  const onLineChartDownload = useCallback(
    async (exportFormats: ExportFormat[]): Promise<void> => {
      if (!averagePriceParam) {
        return;
      }

      const exportImage = exportImages[generateUniqueChartKeys(averagePrice)];

      try {
        const data: AveragePriceChartResp = await getAverageSellingPriceChart({
          ...averagePriceParam,
        });
        const lineChartOptions: ExportLineChartOptions = {
          exportFormats,
          invokeExport: invokeExportLineChart,
          exportImage,
          title: AVERAGE_SELLING_PRICE,
          appliedCountry: appliedCountries,
          appliedPg: appliedProductGroups,
          data: data,
          timePeriod: timePeriod,
          comparisonTimePeriod: comparisonTimePeriod,
          factRevenue: factRevenueBySelectedCurrency,
          periodType: periodType,
          showPerformanceWithin: '',
          appliedFiltersText: appliedFiltersText,
          selectedKpi: selectedKpiOption,
          chartTitles: [AVERAGE_SELLING_PRICE],
          chartSubTitles: [],
          pageName: PageName.RETAILER_OVERVIEW,
        };
        await exportLineChart(lineChartOptions);
        handleExportedTracking(ElementType.LINE_CHART, exportFormats);
      } catch (err: any) {
        const errorMessage = 'Retailer Overview - export Average Selling Price error.';
        displayExportErrorToastMessage(err, errorMessage, requestPayload, showToastMessage);
        return;
      }
    },
    [averagePriceParam, averagePrice, exportImages],
  );
  //End Export Functionality

  const getCollectionParams = (): {
    clientId: number;
    productType: ProductType;
    productGroup: Option[];
    period: string;
    countries: Country[];
    comparisonPeriod: RelativePeriod;
    periodType: Periodicity;
    saleType: PerformanceType;
    appliedFilters: Filters[];
    isSummary: boolean;
  } => {
    const { productGroup, appliedFilters } = filterByAccessibleProductGroup(
      pagesConfig.appliedFilters[PageType.RETAILER_OVERVIEW].selectedFilters,
      pagesConfig.pgsByAppliedCountry,
      periodType,
      period.value,
      appliedCountries,
      getPgToQuery() ?? [],
    );

    return {
      clientId: retailerClientState.clientId,
      productType: effectiveProductType,
      productGroup: productGroup,
      countries: [appliedCountry()!],
      period: period.value,
      comparisonPeriod: comparisonPeriod as RelativePeriod,
      periodType: periodicity,
      saleType: shareVsValueVolumeFilterType,
      appliedFilters: appliedFilters,
      isSummary: false,
    };
  };

  const createCombinedChartInsightData: CreateInsightDataFn<RetailerOverviewCombinedChartInsight> = (
    collectionDataType: CollectionDataType = 'live',
  ) => {
    return {
      apiParams: getCollectionParams(),
      factRevenue: factRevenueBySelectedCurrency,
      volume: getVolumeBasedOnPeriodType(filters, periodicity),
      selectedKPIOption: shareVsValueVolumeFilterType,
      collectionDataType: collectionDataType,
      retailerClientName: retailerClientState.clientName,
    };
  };

  const createLineChartInsightData: CreateInsightDataFn<RetailerOverviewLineChartInsight> = (
    collectionDataType: CollectionDataType = 'live',
  ) => {
    return {
      apiParams: getCollectionParams(),
      factRevenue: factRevenueBySelectedCurrency,
      volume: getVolumeBasedOnPeriodType(filters, periodicity),
      collectionDataType: collectionDataType,
      retailerClientName: retailerClientState.clientName,
    };
  };

  const getSaveViewFilters = () => {
    const handledTimeFrame = getPeriodicityForMATAndYTD(period, periodicity);
    return {
      periodicity: handledTimeFrame.periodicity,
      matYtdTimePeriodLabel: handledTimeFrame.matYtdLabel,
      comparisonPeriod: isBaseTimePeriodFormat(comparisonPeriod)
        ? RelativePeriodLabel.PreviousPeriod
        : RelativePeriodMapping[comparisonPeriod as RelativePeriod],
      growthType: selectedGrowthType.charAt(0).toUpperCase() + selectedGrowthType.slice(1),
      appliedFilters: pagesConfig.appliedFilters[PageType.RETAILER_OVERVIEW].selectedFilters,
      retailerClientState: retailerClientState,
      initialFilterState: initialFiltersState,
      appliedCountries: appliedCountries,
      hitlistRetailerClientState: hitlistsRetailerClientState,
      hitlistInitialFilterState: initialFiltersProductsState,
      mainFilter: mainFiltersState,
      productMainFilterState: mainFilterStateProducts,
      currencyFilters: [],
    };
  };

  const restoreLocalViewProps: RestoreLocalSavedViewProps = {
    productType: effectiveProductType,
    setIsSubscribed: () => {},
    setGrowthOption: () => {},
    setTimeFrame: updatePeriodicity,
    setPeriod: updatePeriod,
    setRelativePeriod: updateComparisonPeriod,
    setFilters: () => {},
    setSelectedCountries,
    setAppliedCountries,
    setDefaultFilters: () => {},
    setSelectedProductGroupOptions: () => {},
    setAppliedProductGroupOptions: () => {},
  };

  // Call restore saved views hooks to restore
  useRestoreRetailerOverviewSavedView(restoreLocalViewProps);

  return (
    <div>
      <Page>
        <Page.Title>
          <div className="pt-8">
            <PageHeader
              retailerClients={retailerClients}
              onChangeHandler={onChangeHandler}
              productType={ProductType.PerformanceAnalyzer}
            >
              <div className="grid grid-cols-2">
                <div className="col-start-1">
                  <h1 className="text-heading text-4xl font-normal m-0">
                    <span>Retailer Overview</span>
                    <PageInfo
                      title={'Retailer Overview'}
                      content={
                        <section>
                          <p>Capture a deeper view of Product Group performance against the market. </p>
                          <p>
                            See the complete picture of your performance across the whole market, with highlighted KPI
                            metrics and topline trends on share & price positioning. Use Filters to narrow your view to
                            a category or group of categories of your choosing – create the sector views that matter to
                            you!
                          </p>
                        </section>
                      }
                    />
                  </h1>
                </div>
                <div className="col-start-5">
                  <div className="flex justify-end">
                    <FilterToggleButton onClick={onClick} testId="filter-button" />
                  </div>
                </div>
              </div>
            </PageHeader>
          </div>
        </Page.Title>
        <Page.Content>
          <InfoModal />
          {isAuthorizedUser ? (
            <>
              <LoadingOverlay loadingOverlay={loadingOverlay} />
              <div className="flex items-end pt-6 pb-4">
                <div className={`${styles.Overview__Controls} grid w-full`}>
                  <TimeFramePickerWrapper
                    isPeriodOptionsDisabled={false}
                    isTimeFramePickerDisabled={isFetchingApi}
                    currentPeriod={period}
                    currentPeriodicity={periodicity}
                    currentComparison={comparisonPeriod}
                    periodOptions={periodOptions}
                    applyPickerCallback={applyTimeFramePickerCallback}
                    appliedCountries={appliedCountries}
                    appliedProductGroups={appliedProductGroups}
                  />
                </div>
              </div>
              <div className="pb-4">
                <FilterSelectionNote
                  selectedPeriod={period}
                  comparisonPeriodType={comparisonPeriod}
                  selectedPeriodicity={periodicity}
                  scopeMetadata={filteredScopeData[0]?.scopeMetadata}
                />
              </div>
              <div>
                {<PgAvailabilityBanner />}
                <WarningProductGroupFilterContainer pagesConfig={pagesConfig} />
                <div className="pt-4">
                  <FilterChips pgListByCountry={pagesConfig.pgsByAppliedCountry} selectedTimeFrame={periodicity} />
                </div>
              </div>
              <div data-test-id={PERFORMANCE_CARD_TITLE} className="pt-6">
                {!isDataError(salesPerformance) ? (
                  <PerformanceFilter
                    selectedPerformanceFilter={performanceType}
                    changePerformanceFilter={setPerformanceType}
                    getPerformanceCardResponse={(type: any) => getPerformanceCardResponse(type)}
                    volume={getVolumeBasedOnPeriodType(filters, periodicity)}
                    isLoading={loading}
                    factRevenue={factRevenueBySelectedCurrency}
                  />
                ) : null}
                <MarketPerformancePosFactsTiles
                  selectedGrowthType={GrowthTypes.percentage}
                  isLoading={loading}
                  performanceType={performanceType}
                  revenuePerformance={salesPerformance}
                  volumePerformance={salesPerformance}
                  factRevenue={factRevenueBySelectedCurrency}
                  volume={getVolumeBasedOnPeriodType(filters, periodicity)}
                  isApiError={isApiError}
                />
              </div>
              {!onPageFirstLoad && (
                <div className={`mt-6 ${styles.Overview__Performance_Container}`}>
                  <ShareComparisonCard
                    testId="Performance-card"
                    onShareRadioChange={setShareVsValueVolumeFilterType}
                    data={shareVsValueVolume}
                    selectedPeriodicity={periodicity}
                    selectedPerformanceType={shareVsValueVolumeFilterType}
                    isLoading={loadingShareVsValueVolume}
                    factRevenue={factRevenueBySelectedCurrency}
                    volume={getVolumeBasedOnPeriodType(filters, periodicity)}
                    isRetailerOverview={true}
                    onChartDownload={onCombinedChartDownload}
                    onExportReady={onExportReadyCombinedChart}
                    createInsightDataFn={createCombinedChartInsightData}
                    overviewState={getSaveViewFilters()}
                    isApiError={isApiError}
                  />
                </div>
              )}
              <div className="mt-6">
                <AveragePriceCard
                  data={averagePrice}
                  selectedPeriodicity={periodicity}
                  isLoading={loadingAveragePrice}
                  factRevenue={factRevenueBySelectedCurrency}
                  volume={getVolumeBasedOnPeriodType(filters, periodicity)}
                  isRetailerOverview={true}
                  onChartDownload={onLineChartDownload}
                  createInsightDataFn={createLineChartInsightData}
                  overviewState={getSaveViewFilters()}
                />
              </div>
            </>
          ) : (
            Unauthorized()
          )}
        </Page.Content>
      </Page>
    </div>
  );
};

export default Overview;
